/**
 * AR Vision Demo — Scene 7–8 voice-call UI store.
 *
 * THROWAWAY DEMO CODE. A tiny external store (built on `useSyncExternalStore`,
 * no extra deps) holding the transient UI state for the overdue-customer beat:
 *  - whether the "Call Cascade Manufacturing" recommendation card is showing
 *    (fired by the `ar_recommend_call` tool), and
 *  - the live/scripted call state (status + streamed transcript), fired by the
 *    `ar_place_call` tool.
 *
 * The demo tools run imperatively from the chat interceptor (outside React), so
 * we keep this state in a module-scoped store rather than component state. The
 * `<ArVoiceCallOverlay>` subscribes for reactive rendering. A demo reset clears
 * it back to the idle defaults.
 */

import { useCallback, useSyncExternalStore } from 'react';
import type { DemoCallTranscriptLine } from './fixture-store.types';

/** Phase of the on-screen call. */
export type VoiceCallPhase =
  | 'idle'
  | 'dialing'
  | 'connected'
  | 'ended'
  | 'failed';

export interface VoiceCallUiState {
  /** Show the AI recommendation card on the overdue customer's profile. */
  recommendationVisible: boolean;
  /** Show the on-screen call panel. */
  callVisible: boolean;
  phase: VoiceCallPhase;
  /** True when the call is driven by a real Vapi call (vs scripted fallback). */
  isLive: boolean;
  /**
   * The live Vapi call id, once placed. The call panel subscribes to the SSE
   * transcript stream for this id (live path); null for the scripted fallback.
   */
  liveCallId: string | null;
  /** Transcript lines revealed so far (scripted fallback only). */
  transcript: DemoCallTranscriptLine[];
  /** AI-style summary, set once the call ends. */
  summary: string | null;
  /** Human-readable status note (e.g. "Live call unavailable — replaying"). */
  note: string | null;
}

const IDLE: VoiceCallUiState = {
  recommendationVisible: false,
  callVisible: false,
  phase: 'idle',
  isLive: false,
  liveCallId: null,
  transcript: [],
  summary: null,
  note: null,
};

let state: VoiceCallUiState = { ...IDLE };
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function set(patch: Partial<VoiceCallUiState>): void {
  state = { ...state, ...patch };
  emit();
}

export function getVoiceCallState(): VoiceCallUiState {
  return state;
}

export function showCallRecommendation(): void {
  set({ recommendationVisible: true });
}

export function beginCall(opts: { isLive: boolean; note?: string }): void {
  set({
    callVisible: true,
    phase: 'dialing',
    isLive: opts.isLive,
    liveCallId: null,
    transcript: [],
    summary: null,
    note: opts.note ?? null,
  });
}

/** Record the placed Vapi call id so the panel can stream its transcript. */
export function setLiveCallId(callId: string): void {
  set({ liveCallId: callId });
}

export function setCallPhase(phase: VoiceCallPhase, note?: string): void {
  set({ phase, ...(note !== undefined ? { note } : {}) });
}

export function appendTranscriptLine(line: DemoCallTranscriptLine): void {
  set({ transcript: [...state.transcript, line] });
}

export function setTranscript(lines: DemoCallTranscriptLine[]): void {
  set({ transcript: lines });
}

export function endCall(summary: string | null): void {
  set({ phase: 'ended', summary });
}

export function resetVoiceCall(): void {
  state = { ...IDLE };
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useVoiceCallStore<T>(selector: (s: VoiceCallUiState) => T): T {
  const getSnapshot = useCallback(() => selector(state), [selector]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
